import { getCallsList, getCallDetails, getAllCallsForRequest } from '../utils/apiClient';

/**
 * Call History Service
 * Provides handy functions for fetching and processing call history data
 */

// ============================================
// Data Fetching Functions
// ============================================

/**
 * Fetches paginated call list with server-side pagination
 * @param {Object} params - Query parameters
 * @param {string} [params.batchId] - Optional batch ID filter
 * @param {number} [params.page] - Page number (zero-based)
 * @param {number} [params.rowsPerPage] - Number of rows per page
 * @param {string} [params.callStatus] - Call status filter (scheduled, cancelled, successful, failure)
 * @returns {Promise<{calls: Array, totalCount: number}>} Processed call list
 */
export const fetchCallsList = async (params) => {
  try {
    const response = await getCallsList({
      batchId: params.batchId,
      limit: params.rowsPerPage,
      skip: params.page * params.rowsPerPage,
      patientName: params.searchQuery,
      callStatus: params.callStatus,
    });

    if (response.statusCode === "SUCCESS" && response.response) {
      return {
        calls: response.response?.calls || [],
        totalCount: response.response?.totalCount || 0,
        message: response.message,
      };
    }

    throw new Error(response.message || 'Failed to fetch calls list');
  } catch (error) {
    console.error('Error in fetchCallsList service:', error);
    throw error;
  }
};

/**
 * Fetches complete call details with transcription
 * @param {string} callRequestId - Call request ID
 * @returns {Promise<Object>} Complete call details
 */
export const fetchCallDetails = async (callRequestId) => {
  try {
    const response = await getCallDetails(callRequestId);

    if (response.statusCode === 'SUCCESS') {
      return {
        success: true,
        data: response.response,
        message: response.message,
      };
    }

    throw new Error(response.message || 'Failed to fetch call details');
  } catch (error) {
    console.error('Error in fetchCallDetails service:', error);
    throw error;
  }
};

/**
 * Fetches ALL calls for a specific call request (all attempts)
 * Returns calls array, whitesheet data, batch metadata, and call request data
 * @param {string} callRequestId - Call request ID
 * @returns {Promise<Object>} All calls and metadata for the call request
 */
export const fetchAllCallsForRequest = async (callRequestId) => {
  try {
    const response = await getAllCallsForRequest(callRequestId);

    if (response.statusCode === 'SUCCESS') {

      
      return {
        success: true,
        calls: response.response?.calls || [],
        whitesheetData: response.response?.whitesheetData || null,
        batchMetadata: response.response?.batchMetadata || null,
        callRequestData: response.response?.callRequestData || null,
        cancelBefore: response.response?.cancelBefore || null,
        faxSignedUrl: response.response?.faxSignedUrl || null,
        message: response.message,
      };
    }

    throw new Error(response.message || 'Failed to fetch calls for request');
  } catch (error) {
    console.error('Error in fetchAllCallsForRequest service:', error);
    return {
      success: false,
      error: error.message || 'An error occurred',
      calls: [],
    };
  }
};

// ============================================
// Data Processing & Formatting Functions
// ============================================

/**
 * Formats date/time to readable string
 * @param {string} dateString - ISO date string
 * @param {Object} [options] - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDateTime = (dateString, options = {}) => {
  if (!dateString) return '—';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  };

  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', defaultOptions);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '—';
  }
};

/**
 * Formats date only (no time)
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return '—';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '—';
  }
};

/**
 * Formats time only (no date)
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted time string
 */
export const formatTime = (dateString) => {
  if (!dateString) return '—';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return '—';
  }
};

/**
 * Formats duration from seconds to mm:ss or hh:mm:ss
 * @param {number} seconds - Duration in seconds
 * @param {boolean} [includeHours=false] - Include hours in format
 * @returns {string} Formatted duration string
 */
export const formatDuration = (seconds, includeHours = false) => {
  if (!seconds || seconds === 0) return '—';
  
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (includeHours || hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Formats duration object to readable string
 * @param {Object} callDuration - Duration object with totalDuration, callerDuration, etc.
 * @returns {string} Formatted duration string
 */
export const formatCallDuration = (callDuration) => {
  if (!callDuration || !callDuration.totalDuration) return '—';
  return formatDuration(callDuration.totalDuration);
};

/**
 * Gets full member name from member details
 * @param {Object} member - Member object
 * @returns {string} Full member name
 */
export const getFullMemberName = (member) => {
  if (!member) return '—';
  
  const firstName = member.memberFirstName || '';
  const middleName = member.memberMiddleName || '';
  const lastName = member.memberLastName || '';
  
  const fullName = `${firstName} ${middleName} ${lastName}`.trim();
  return fullName || '—';
};

/**
 * Gets call status display info (color, label)
 * @param {string} status - Call status
 * @returns {Object} Status display info
 */
export const getCallStatusInfo = (status) => {
  const statusMap = {
    completed: {
      label: 'Completed',
      color: '#4caf50',
      bgColor: 'rgba(76, 175, 80, 0.1)',
    },
    pending: {
      label: 'Pending',
      color: '#ff9800',
      bgColor: 'rgba(255, 152, 0, 0.1)',
    },
    scheduled: {
      label: 'Scheduled',
      color: '#2196f3',
      bgColor: 'rgba(33, 150, 243, 0.1)',
    },
    failed: {
      label: 'Failed',
      color: '#f44336',
      bgColor: 'rgba(244, 67, 54, 0.1)',
    },
    cancelled: {
      label: 'Cancelled',
      color: '#9e9e9e',
      bgColor: 'rgba(158, 158, 158, 0.1)',
    },
  };

  return statusMap[status?.toLowerCase()] || {
    label: status || 'Unknown',
    color: '#757575',
    bgColor: 'rgba(117, 117, 117, 0.1)',
  };
};

/**
 * Gets call outcome display info (color, label)
 * @param {string} outcome - Call outcome
 * @returns {Object} Outcome display info
 */
export const getCallOutcomeInfo = (outcome) => {
  if (!outcome) {
    return {
      label: '—',
      color: '#757575',
      bgColor: 'transparent',
    };
  }

  const isSuccessful = outcome.toLowerCase() === 'successful';
  return {
    label: outcome,
    color: isSuccessful ? '#4caf50' : '#f44336',
    bgColor: isSuccessful ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
  };
};

/**
 * Gets speaker type label
 * @param {string} speaker - Speaker identifier
 * @returns {string} Speaker label
 */
export const getSpeakerLabel = (speaker) => {
  const speakerMap = {
    agent: 'AI Agent',
    bot: 'Bot',
    representative: 'Representative',
    ivr: 'IVR',
    caller: 'Caller',
    callee: 'Callee',
  };

  return speakerMap[speaker?.toLowerCase()] || speaker || 'Unknown';
};

/**
 * Processes transcription data for display
 * @param {Array} transcription - Raw transcription array
 * @returns {Array} Processed transcription
 */
export const processTranscription = (transcription) => {
  if (!Array.isArray(transcription)) return [];

  return transcription.map((item, index) => ({
    id: item._id || `transcript-${index}`,
    speaker: getSpeakerLabel(item.speaker),
    rawSpeaker: item.speaker,
    text: item.utterance || item.rawUtterance || '',
    startTime: item.startTime,
    endTime: item.endTime,
    duration: item.durationSeconds || 0,
    confidence: item.confidence,
    isReviewed: item.isReviewed || false,
    formattedStartTime: formatTime(item.startTime),
    formattedEndTime: formatTime(item.endTime),
  }));
};

/**
 * Processes questions discussed for display
 * @param {Array} questions - Raw questions array
 * @returns {Array} Processed questions
 */
export const processQuestionsDiscussed = (questions) => {
  if (!Array.isArray(questions)) return [];

  return questions.map((q, index) => ({
    id: q.sequence_id || `question-${index}`,
    sequenceId: q.sequence_id,
    question: q.question_text,
    wasDiscussed: q.was_discussed,
    answered: q.answered,
    answer: q.answer || '—',
    comments: q.comments || '',
    category: q.sequence_id?.split('.')[0] || 'other',
  }));
};

/**
 * Gets whitesheet summary data
 * @param {Object} whitesheetData - Raw whitesheet data
 * @returns {Object} Processed whitesheet summary
 */
export const getWhitesheetSummary = (whitesheetData) => {
  if (!whitesheetData) return null;

  return {
    generated: whitesheetData.generated || false,
    reviewed: whitesheetData.reviewed || false,
    reviewedBy: whitesheetData.reviewedBy,
    callDetails: whitesheetData.predicted?.call_details || {},
    detailedBreakdown: whitesheetData.predicted?.detailed_breakdown?.[0] || {},
    memberDetails: whitesheetData.predicted?.member_details || {},
    finalData: whitesheetData.final || {},
  };
};

/**
 * Checks if call has transcription
 * @param {Object} callData - Call data object
 * @returns {boolean} True if transcription exists
 */
export const hasTranscription = (callData) => {
  return Array.isArray(callData?.transcription) && callData.transcription.length > 0;
};

/**
 * Checks if call has recording
 * @param {Object} callData - Call data object
 * @returns {boolean} True if recording exists
 */
export const hasRecording = (callData) => {
  return Boolean(callData?.callRecordingUrl || callData?.callRecordingPath);
};

/**
 * Gets call attempt info
 * @param {number} attempts - Number of attempts
 * @returns {Object} Attempt info
 */
export const getAttemptInfo = (attempts) => {
  return {
    count: attempts || 0,
    label: attempts === 1 ? '1 attempt' : `${attempts || 0} attempts`,
    color: attempts > 1 ? '#ff9800' : '#4caf50',
  };
};

/**
 * Formats batch metadata for display
 * @param {Object} batchMetadata - Raw batch metadata
 * @returns {Object} Formatted batch metadata
 */
export const formatBatchMetadata = (batchMetadata) => {
  if (!batchMetadata) return null;

  return {
    uploadedBy: batchMetadata.uploadedBy,
    branch: batchMetadata.branch,
    usecase: batchMetadata.usecase?.replace(/_/g, ' ') || '',
    subUsecase: batchMetadata.subUsecase,
    startDateTime: formatDateTime(batchMetadata.startDateTime),
    source: batchMetadata.source,
    scheduledSlots: batchMetadata.scheduledSlotsUtc || [],
  };
};

/**
 * Gets call connection status info
 * @param {string} connection - Connection status
 * @returns {Object} Connection status info
 */
export const getConnectionStatusInfo = (connection) => {
  const statusMap = {
    connected: {
      label: 'Connected',
      color: '#4caf50',
      icon: '✓',
    },
    disconnected: {
      label: 'Disconnected',
      color: '#f44336',
      icon: '✗',
    },
    pending: {
      label: 'Pending',
      color: '#ff9800',
      icon: '⟳',
    },
  };

  return statusMap[connection?.toLowerCase()] || {
    label: connection || 'Unknown',
    color: '#757575',
    icon: '?',
  };
};

/**
 * Processes call list for table display
 * @param {Array} calls - Raw calls array
 * @returns {Array} Processed calls for display
 */
export const processCallsForTable = (calls) => {
  if (!Array.isArray(calls)) return [];

  return calls.map((call) => ({
    ...call,
    fullMemberName: getFullMemberName(call),
    formattedStartTime: formatDateTime(call.callStartTime),
    formattedEndTime: formatDateTime(call.callEndTime),
    formattedDuration: formatDuration(call.totalDuration),
    statusInfo: getCallStatusInfo(call.callStatus),
    outcomeInfo: getCallOutcomeInfo(call.callOutcome),
  }));
};

// ============================================
// Export all functions
// ============================================

export default {
  // Data fetching
  fetchCallsList,
  fetchCallDetails,
  
  // Date/time formatting
  formatDateTime,
  formatDate,
  formatTime,
  formatDuration,
  formatCallDuration,
  
  // Data processing
  getFullMemberName,
  getCallStatusInfo,
  getCallOutcomeInfo,
  getSpeakerLabel,
  processTranscription,
  processQuestionsDiscussed,
  getWhitesheetSummary,
  formatBatchMetadata,
  getConnectionStatusInfo,
  getAttemptInfo,
  processCallsForTable,
  
  // Helpers
  hasTranscription,
  hasRecording,
};
